import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef, useCallback, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { FolderTriageDialog, type FolderTriagePending } from '@/components/FolderTriageDialog';
import { compressImageFile, compressImagesInFiles } from '@/lib/imageResize';
import {
  collectFilesFromDirectoryInput,
  collectFilesFromDrop,
  fileDisplayLabel,
  triageDocuments,
  TRIAGE_MAX_FILES,
  type TriagedFile,
} from '@/lib/folderTriage';
import { setPendingFiles } from '@/lib/pendingFileTransfer';
import { brandColors } from '@/theme/brandColors';

const PREFILL_SESSION_KEY = 'immokompass_prefill_title';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/** Muss zum Limit der AnalysePage passen — dort wird ohnehin auf 15 gekappt. */
const MAX_FILES = TRIAGE_MAX_FILES;
const ACCEPTED_MIME = ['application/pdf', 'application/x-pdf'];

function isAcceptedFile(f: File): boolean {
  const t = (f.type || '').toLowerCase();
  if (ACCEPTED_MIME.includes(t) || t.startsWith('image/')) return true;
  return /\.(pdf|jpe?g|png|webp|gif|bmp|tiff?)$/i.test(f.name.trim());
}

type Props = {
  open: boolean;
  onClose: () => void;
  navigate: NavigateFunction;
};

export function AddImmobilieModal({ open, onClose, navigate }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [drag, setDrag] = useState(false);
  const [pendingTriage, setPendingTriage] = useState<FolderTriagePending | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleClose = useCallback(() => {
    setFiles([]);
    setDrag(false);
    setPendingTriage(null);
    onClose();
  }, [onClose]);

  const applyFileList = useCallback((list: FileList | File[]) => {
    const arr = Array.from(list).filter(isAcceptedFile);
    setFiles((prev) => {
      const merged = [...prev, ...arr];
      const unique = merged.filter(
        (f, i, a) => a.findIndex((x) => x.name === f.name && x.size === f.size) === i
      );
      return unique.slice(0, MAX_FILES);
    });
  }, []);

  /**
   * Ordner-Inhalte (rekursiv eingesammelt) triagieren. Bei Ordner-Uploads
   * oder Aussortiertem erst den App-eigenen Bestätigungsdialog zeigen;
   * simple Datei-Drops werden wie bisher direkt übernommen.
   */
  const ingestTriaged = useCallback(
    async (all: TriagedFile[]) => {
      // Große Bilder (Handy-Fotos) vor Triage/Dialog clientseitig komprimieren.
      const compacted = await Promise.all(
        all.map(async (t) => {
          const file = await compressImageFile(t.file);
          return { file, relPath: file === t.file ? t.relPath : t.relPath.replace(/[^/]+$/, file.name) };
        })
      );
      const { selected, skipped } = triageDocuments(compacted, { maxFiles: MAX_FILES });
      const wasFolder = all.some((t) => t.relPath.includes('/'));
      if (wasFolder || skipped.length > 0) {
        setPendingTriage({ selected, skipped });
      } else if (selected.length) {
        applyFileList(selected);
      }
    },
    [applyFileList]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDrag(false);
      // Synchron aufrufen (webkitGetAsEntry) — expandiert auch gedroppte Ordner.
      const collected = collectFilesFromDrop(e.dataTransfer);
      void collected.then((all) => {
        if (all.length) ingestTriaged(all);
      });
    },
    [ingestTriaged]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const submit = useCallback(() => {
    if (files.length > 0) setPendingFiles(files);
    navigate('/analyse');
    setFiles([]);
    onClose();
  }, [files, navigate, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.4)' } }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        },
      }}
    >
      <DialogTitle sx={{ pr: 5, pt: 2.5, pb: 1 }}>
        <IconButton
          aria-label="Schließen"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            bgcolor: `rgba(${brandColors.primaryRgb}, 0.12)`,
            color: 'primary.main',
            fontSize: '0.75rem',
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          <RocketLaunchOutlinedIcon sx={{ fontSize: 16 }} />
          Neue Analyse
        </Box>
        <Typography component="span" variant="h2" sx={{ display: 'block', fontWeight: 700, fontSize: '1.25rem' }}>
          Unterlagen hochladen
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontSize: '0.8125rem' }}>
          Lade deine Immobilien-Unterlagen hoch — Name und Adresse werden automatisch aus den Dokumenten erkannt.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff"
          style={{ display: 'none' }}
          onChange={(e) => {
            const picked = e.target.files?.length ? Array.from(e.target.files) : [];
            e.target.value = '';
            if (picked.length) void compressImagesInFiles(picked).then((cs) => applyFileList(cs));
          }}
        />
        {/* Ordner-Upload: expandiert den Ordner rekursiv, Triage filtert
            Fotos/Junk raus und priorisiert die wichtigsten Unterlagen. */}
        <input
          ref={folderInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
          onChange={(e) => {
            const picked = e.target.files?.length ? collectFilesFromDirectoryInput(e.target.files) : [];
            e.target.value = '';
            if (picked.length) ingestTriaged(picked);
          }}
        />

        {/* Drop zone */}
        <Box
          onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: drag ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: drag ? `rgba(${brandColors.primaryRgb}, 0.06)` : 'background.default',
            py: 4,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: `rgba(${brandColors.primaryRgb}, 0.04)`,
            },
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: drag ? 'primary.main' : 'text.disabled' }} />
          <Typography variant="body2" fontWeight={600} color={drag ? 'primary.main' : 'text.primary'}>
            Dateien oder ganzen Ordner hier ablegen
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unterordner werden automatisch durchsucht — oder klicken zum Auswählen (PDF, JPG, PNG, max. {MAX_FILES} Dateien)
          </Typography>
          <Button
            size="small"
            color="inherit"
            startIcon={<FolderOpenOutlinedIcon />}
            onClick={(e) => {
              e.stopPropagation();
              folderInputRef.current?.click();
            }}
            sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Ordner über Dialog auswählen
          </Button>
        </Box>

        <FolderTriageDialog
          pending={pendingTriage}
          onConfirm={(sel) => {
            applyFileList(sel);
            setPendingTriage(null);
          }}
          onCancel={() => setPendingTriage(null)}
        />

        {/* File list */}
        {files.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {files.map((f, i) => (
              <Chip
                key={`${f.name}-${f.size}-${i}`}
                icon={<InsertDriveFileOutlinedIcon />}
                label={fileDisplayLabel(f)}
                size="small"
                onDelete={() => removeFile(i)}
                sx={{ maxWidth: 220, fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          onClick={handleClose}
          fullWidth
          variant="outlined"
          sx={{ flex: { sm: '1 1 120px' }, borderColor: '#e5e7eb', color: 'text.primary', borderRadius: 1.5 }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={submit}
          fullWidth
          variant="contained"
          sx={{ flex: { sm: '1 1 160px' }, borderRadius: 1.5, fontWeight: 600 }}
        >
          {files.length > 0 ? `${files.length} Datei${files.length !== 1 ? 'en' : ''} analysieren` : 'Ohne Dateien starten'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function readPrefillTitleFromSession(): string | null {
  try {
    const v = sessionStorage.getItem(PREFILL_SESSION_KEY);
    return v?.trim() || null;
  } catch {
    return null;
  }
}

export function clearPrefillTitleSession(): void {
  try {
    sessionStorage.removeItem(PREFILL_SESSION_KEY);
  } catch {
    /* ignore */
  }
}
